import Image from 'next/image';
import cn from 'classnames';

import Heading from '@components/ui/Heading';
import { trackPlpHeroCtaClick } from '_utils/GA4/trackPlpHeroEvents';
import NavCarousel from './NavCarousel';
import { PlpHeroData } from './types';

import styles from './PlpHero.module.scss';

export interface PlpHeroProps {
  data: PlpHeroData;
  locale?: string;
}

const PlpHero = ({ data, locale }: PlpHeroProps) => {
  const {
    h1,
    description,
    image,
    mobileImage,
    navCarousel,
    callToActions,
    coOpBrand,
  } = data;

  // Either upload alone is enough for a cover hero — the other breakpoint
  // reuses it. Documents authored before the mobile upload existed only have
  // `image`, and an editor who fills just the mobile one still gets a hero
  // rather than silently dropping to the text-only variant.
  const desktopImageUrl = image?.url ?? mobileImage?.url ?? null;
  const mobileImageUrl = mobileImage?.url ?? image?.url ?? null;
  const hasImage = !!desktopImageUrl;
  // Only worth two <img> elements when the uploads actually differ; a single
  // one avoids the second preload and the breakpoint toggle.
  const hasDistinctImages =
    !!desktopImageUrl && !!mobileImageUrl && desktopImageUrl !== mobileImageUrl;

  return (
    <section
      className={cn(styles.hero, hasImage ? styles.withImage : styles.textOnly)}
      data-testid="plp-hero"
    >
      {hasImage && (
        <>
          <div className={styles.background}>
            <Image
              priority
              src={desktopImageUrl as string}
              alt=""
              fill
              sizes="100vw"
              className={cn(
                styles.backgroundImage,
                hasDistinctImages && styles.desktopImage
              )}
            />
            {hasDistinctImages && (
              <Image
                priority
                src={mobileImageUrl as string}
                alt=""
                fill
                sizes="100vw"
                className={cn(styles.backgroundImage, styles.mobileImage)}
              />
            )}
          </div>
          <div className={styles.overlay} aria-hidden="true" />
        </>
      )}
      <div className={styles.content}>
        <div className={styles.text}>
          <Heading semanticLevel="h1" styleLevel="1" className={styles.title}>
            {h1}
          </Heading>
          {description && <p className={styles.description}>{description}</p>}
          {callToActions?.length > 0 && (
            <div className={styles.ctaContainer}>
              {callToActions.map(
                ({ cta_text: ctaText, cta_url: ctaUrl }, index) => {
                  const href = `/${locale}${ctaUrl}`;

                  return (
                    <a
                      key={ctaText}
                      href={href}
                      className={styles.cta}
                      onClick={() =>
                        trackPlpHeroCtaClick({
                          data: {
                            // Read at click time rather than from the router so
                            // the path reflects where the user actually was
                            interactionLocation: window.location.pathname,
                            linkUrl: href,
                            linkText: ctaText,
                            index,
                            coOpBrand,
                          },
                        })
                      }
                    >
                      {ctaText}
                    </a>
                  );
                }
              )}
            </div>
          )}
        </div>
        {navCarousel && (
          <NavCarousel
            navCarousel={navCarousel}
            onImage={hasImage}
            locale={locale}
            coOpBrand={coOpBrand}
          />
        )}
      </div>
    </section>
  );
};

export default PlpHero;
